import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { addDoc, collection, doc, getDocs, orderBy, query } from "firebase/firestore";
import { usePermissoesAcesso } from "../Utils/permissoesAcesso";
import { useHistory } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import useEstadoSalvamento from "../Utils/useEstadoSalvamento";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import PaginationComponent from "../Components/PaginationComponents/paginationcomponents";
import PaginationSelector from "../Components/PaginationSelector/paginationselector";
import { auth, db } from "../Config/firebase";
import { gerarSequencial, obterMaiorSequencial } from "../Utils/sequencial";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";
import { atualizarDocumentoComMesclagem } from "../Utils/concorrenciaFirestore";

const estadoInicial = {
  codigo: "",
  complementoCodigo: "",
  clienteId: "",
  obraId: "",
  data: "",
  materialIds: [],
  cimentoId: "",
  aditivoIds: [],
  adicaoIds: [],
  aguaId: "",
  pesquisarTodosMateriais: false,
  pesquisarTodosCimentos: false,
  pesquisarTodosAditivos: false,
  pesquisarTodosAdicoes: false,
  traco: "",
  numeroRevisao: "0",
  fatorAc: "",
  fck: "",
  sd: "",
  slump: "",
  toleranciaSlumpProjeto: "",
  tipoConcreto: "",
  cps: "",
  diametroCp: "100",
  caldaCimento: false,
  cancelada: false,
  motivoCancelamento: "",
  teorArConsiderado: "0,0",
  volumeBetoneira: "",
  usarTextosAutomaticosObservacoes: true,
};

function obterDiametroCpCadastro(dosagem = {}) {
  const diametroInformado = String(dosagem.diametroCp ?? "").trim();
  return diametroInformado || (dosagem.caldaCimento === true ? "50" : "100");
}

function areaCpPorDiametro(diametro) {
  const valor = Number(String(diametro || "").replace(",", "."));
  if (!Number.isFinite(valor) || valor <= 0) return "";

  const raioCm = valor / 20;
  return (Math.PI * raioCm * raioCm).toLocaleString("pt-BR", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

function formatarDataBR(data) {
  if (!data) return "-";
  if (typeof data === "string" && data.includes("-")) {
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  }
  if (data?.seconds) return new Date(data.seconds * 1000).toLocaleDateString("pt-BR");
  if (data?.toDate) return data.toDate().toLocaleDateString("pt-BR");
  return String(data);
}

function dataSemHorario(valor) {
  if (!valor) return null;

  if (typeof valor === "string" && /^\d{4}-\d{2}-\d{2}$/.test(valor)) {
    const [ano, mes, dia] = valor.split("-").map(Number);
    return new Date(ano, mes - 1, dia);
  }

  if (valor?.seconds) {
    const data = new Date(valor.seconds * 1000);
    return new Date(data.getFullYear(), data.getMonth(), data.getDate());
  }

  if (valor?.toDate) {
    const data = valor.toDate();
    return new Date(data.getFullYear(), data.getMonth(), data.getDate());
  }

  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return null;
  return new Date(data.getFullYear(), data.getMonth(), data.getDate());
}

function formatarDataCurtaBR(data) {
  const dataNormalizada = dataSemHorario(data);
  if (!dataNormalizada) return "-";

  const dia = String(dataNormalizada.getDate()).padStart(2, "0");
  const mes = String(dataNormalizada.getMonth() + 1).padStart(2, "0");
  const ano = String(dataNormalizada.getFullYear()).slice(-2);

  return `${dia}/${mes}/${ano}`;
}

function rupturaDosagemPreenchida(dosagem, index, idadeValor) {
  const valorPreenchido = (valor) =>
    valor !== null && valor !== undefined && String(valor).trim() !== "";

  return [1, 2].some((cp) =>
    [
      dosagem[`rupturaCp${cp}TonIdade${index}`],
      dosagem[`rupturaCp${cp}Idade${index}`],
      dosagem[`rupturaCp${cp}${idadeValor} dias`],
    ].some(valorPreenchido)
  );
}

function datasRupturaDosagem(dosagem = {}) {
  if (dosagem.cancelada === true) return [];

  const dataBase = dataSemHorario(dosagem.data);
  if (!dataBase) return [];

  const hoje = new Date();
  const hojeSemHorario = new Date(
    hoje.getFullYear(),
    hoje.getMonth(),
    hoje.getDate()
  );

  return [0, 1, 2, 3, 4].flatMap((index) => {
    const padrao = ["3", "7", "", "28", ""][index];
    const idadeValor = dosagem[`idadeRuptura${index}`] ?? padrao;
    const idade = numero(idadeValor);

    if (idade === null || idade < 0 || String(idadeValor).trim() === "") return [];

    const dataRuptura = new Date(dataBase);
    dataRuptura.setDate(dataRuptura.getDate() + idade);

    if (rupturaDosagemPreenchida(dosagem, index, idadeValor)) return [];

    let status;
    if (dataRuptura.getTime() < hojeSemHorario.getTime()) status = "vencida";
    else if (dataRuptura.getTime() === hojeSemHorario.getTime()) status = "hoje";
    else status = "futura";

    return [{
      index,
      idade: idadeValor,
      data: dataRuptura,
      status,
    }];
  });
}

function normalizarTexto(valor) {
  return String(valor || "").toLowerCase();
}

function numeroDosagem(valor = "") {
  return String(valor).match(/\d+/)?.[0] || "";
}

function complementoCodigoDosagem(valor = "") {
  return String(valor).trim().toUpperCase().match(/\.([A-Z0-9]{1,5})$/)?.[1] || "";
}

function montarCodigoDosagem(base, complemento = "") {
  const numero = numeroDosagem(base);
  const complementoNormalizado = String(complemento || "").trim().toUpperCase();
  return numero && complementoNormalizado ? `${numero}.${complementoNormalizado}` : numero;
}

function formatarCodigoDosagem(valor = "") {
  const codigo = montarCodigoDosagem(numeroDosagem(valor), complementoCodigoDosagem(valor));
  return codigo ? `T${codigo}` : "-";
}

function descricaoResumoMaterial(item) {
  return String(
    item?.descricao ||
      item?.Descricao ||
      item?.["Descrição"] ||
      item?.["DescriÃ§Ã£o"] ||
      item?.material ||
      item?.amostra ||
      ""
  ).trim();
}

function fornecedorResumoMaterial(item) {
  return String(
    item?.procedencia ||
      item?.fornecedor ||
      item?.Fornecedor ||
      ""
  ).trim();
}

function descricaoMaterialNaLista(item, incluirFornecedor = false) {
  const partes = [codigoMaterial(item), descricaoResumoMaterial(item)];
  if (incluirFornecedor) partes.push(fornecedorResumoMaterial(item));
  return partes.map((parte) => String(parte || "").trim()).filter(Boolean).join(" - ");
}

function textoPesquisaMaterial(item) {
  return [
    codigoMaterial(item),
    descricaoResumoMaterial(item),
    fornecedorResumoMaterial(item),
  ].filter(Boolean).join(" ");
}

function formatarListaMateriais(lista = []) {
  return lista
    .map(textoPesquisaMaterial)
    .filter(Boolean)
    .join(", ");
}

function numero(valor) {
  if (valor === null || valor === undefined || valor === "") return null;
  const texto = String(valor).trim();
  const normalizado = texto.includes(",")
    ? texto.replace(/\./g, "").replace(",", ".")
    : texto;
  const convertido = Number(normalizado);
  return Number.isFinite(convertido) ? convertido : null;
}

function formatarConsumo(valor, casas = 1, unidade = "kg/m³") {
  const convertido = Number(valor);
  if (!Number.isFinite(convertido) || convertido <= 0) return "";
  return `${convertido.toFixed(casas).replace(".", ",")} ${unidade}`;
}

function materialVazio(item) {
  return !item || !descricaoResumoMaterial(item);
}

function itemMaterialComConsumo(nome, consumo, casas = 1, unidade = "kg/m³") {
  const descricao = String(nome || "").trim() || "-";
  return {
    descricao,
    consumo: formatarConsumo(consumo, casas, unidade),
  };
}

function densidadeAgregado(material = {}) {
  return numero(
    material.densidade ||
      material.densidadeMedia ||
      material.massaEspecifica ||
      material.Densidade ||
      material.MassaEspecifica ||
      material.caracterizacaoCompleta?.densidade?.densidadeMedia ||
      material.caracterizacaoCompleta?.densidadeMedia ||
      material.caracterizacaoCompleta?.massaEspecifica
  );
}

function calcularConsumosDosagem(dosagem = {}) {
  const agregados = Array.isArray(dosagem.materiaisResumo) ? dosagem.materiaisResumo : [];
  const adicoes = Array.isArray(dosagem.adicoesResumo) ? dosagem.adicoesResumo : [];
  const aditivos = Array.isArray(dosagem.aditivosResumo) ? dosagem.aditivosResumo : [];
  const pesoCimento = numero(dosagem.cimentoPeso) || 0;
  const pesoAdicao = (index) =>
    materialVazio(adicoes[index])
      ? 0
      : pesoCimento * ((numero(dosagem[`adicaoParcial${index}`]) || 0) / 100);
  const pesoAdicoes = adicoes.reduce((total, _, index) => total + pesoAdicao(index), 0);
  const pesoLigante = pesoCimento + pesoAdicoes;
  const pesoAgua = pesoLigante * (numero(dosagem.fatorAc) || 0);
  const pesoAditivo = (index) => pesoLigante * ((numero(dosagem[`aditivoParcial${index}`]) || 0) / 100);

  const volumeCimento = pesoCimento / (numero(dosagem.cimentoDensidade || dosagem.cimentoResumo?.massaEspecifica || "3,100") || 1);
  const volumeAdicoes = adicoes.reduce(
    (total, adicao, index) => total + pesoAdicao(index) / (numero(adicao?.massaEspecifica) || 1),
    0
  );
  const volumeAgua = pesoAgua / (numero(dosagem.aguaResumo?.massaEspecifica) || 1);
  const volumeAditivo = (index) => pesoAditivo(index) / (numero(aditivos[index]?.massaEspecifica) || 1);
  const volumeAditivos = aditivos.reduce(
    (total, _, index) => total + volumeAditivo(index),
    0
  );
  const volumeAr = (numero(dosagem.teorArConsiderado) ?? 0) * 10;
  const volumeAgregados = Math.max(0, 1000 - volumeCimento - volumeAdicoes - volumeAgua - volumeAditivos - volumeAr);

  const indiceMiudo = agregados.findIndex((material) => material.tipoAgregado === "miudo");
  const percentualMiudo = indiceMiudo >= 0 ? numero(dosagem[`materialParcial${indiceMiudo}`]) || 0 : 0;
  const percentualGraudo = agregados.some((material) => material.tipoAgregado === "graudo")
    ? Math.max(0, 100 - percentualMiudo)
    : 0;
  const percentualParcialAgregado = (material) =>
    material?.tipoAgregado === "graudo" ? percentualGraudo : percentualMiudo;
  const percentualAgregadoTotal = (index) =>
    ((numero(dosagem[`materialProporcaoGrupo${index}`]) || 0) / 100) *
    (percentualParcialAgregado(agregados[index]) / 100);
  const somaParticipacao = agregados.reduce(
    (total, _, index) => total + percentualAgregadoTotal(index),
    0
  );
  const divisorVolumeAgregados = agregados.reduce((total, material, index) => {
    const participacao = somaParticipacao > 0 ? percentualAgregadoTotal(index) / somaParticipacao : 0;
    return total + participacao / (densidadeAgregado(material) || 1);
  }, 0);
  const massaAgregados = divisorVolumeAgregados > 0 ? volumeAgregados / divisorVolumeAgregados : 0;
  const pesoAgregado = (index) => {
    const participacao = somaParticipacao > 0 ? percentualAgregadoTotal(index) / somaParticipacao : 0;
    return massaAgregados * participacao;
  };

  return { pesoCimento, pesoAdicao, pesoAgua, pesoAditivo, pesoAgregado, volumeAgua, volumeAditivo };
}

export function formatarMateriaisComConsumo(dosagem = {}, grupo) {
  const consumos = calcularConsumosDosagem(dosagem);
  if (grupo === "cimento") {
    return [itemMaterialComConsumo(descricaoMaterialNaLista(dosagem.cimentoResumo), consumos.pesoCimento, 0)];
  }
  if (grupo === "agua") {
    return [itemMaterialComConsumo(descricaoMaterialNaLista(dosagem.aguaResumo) || "Água", consumos.volumeAgua, 0, "l/m³")];
  }

  const configuracao = {
    adicoesResumo: { casas: 1, consumo: consumos.pesoAdicao },
    aditivosResumo: { casas: 2, consumo: consumos.volumeAditivo, unidade: "l/m³" },
    materiaisResumo: { casas: 0, consumo: consumos.pesoAgregado },
  }[grupo];

  return (dosagem[grupo] || [])
    .map((material, index) =>
      itemMaterialComConsumo(
        descricaoMaterialNaLista(material, grupo === "materiaisResumo"),
        configuracao.consumo(index),
        configuracao.casas,
        configuracao.unidade || "kg/m³"
      )
    )
    .filter((item) => item.descricao && item.descricao !== "-");
}

export function GrupoMateriais({ rotulo, itens, destaque = false }) {
  const lista = Array.isArray(itens) ? itens.filter((item) => item.descricao && item.descricao !== "-") : [];
  const corTexto = destaque ? "#14532d" : "#64748b";
  const tamanhoFonte = destaque ? "0.72rem" : "0.68rem";

  if (!lista.length) {
    return (
      <Typography component="span" variant="caption" sx={{ color: corTexto, fontWeight: destaque ? 700 : 600, fontSize: tamanhoFonte }}>
        {rotulo}: -
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "grid", rowGap: 0.25 }}>
      {lista.map((item, index) => (
        <Box
          key={`${rotulo}-${index}-${item.descricao}`}
          sx={{
            display: "grid",
            gridTemplateColumns: "64px minmax(0, 1fr) 72px",
            columnGap: 1,
            alignItems: "baseline",
          }}
        >
          <Typography component="span" variant="caption" sx={{ color: corTexto, fontWeight: destaque ? 700 : 600, fontSize: tamanhoFonte }}>
            {index === 0 ? `${rotulo}:` : ""}
          </Typography>
          <Typography component="span" variant="caption" sx={{ color: corTexto, fontWeight: destaque ? 700 : 600, fontSize: tamanhoFonte, wordBreak: "break-word" }}>
            {item.descricao}
          </Typography>
          <Typography component="span" variant="caption" sx={{ color: "#334155", fontWeight: 700, fontSize: "0.68rem", textAlign: "left", whiteSpace: "nowrap" }}>
            {item.consumo || "-"}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function obterNumeroRevisao(dosagem = {}) {
  return String(
    dosagem.numeroRevisao ??
      String(dosagem.traco || "").match(/REV\.?\s*(\d+)/i)?.[1] ??
      "0"
  ).replace(/\D/g, "") || "0";
}

function normalizarNomeTraco(codigo, numeroRevisao, valor) {
  const codigoFormatado = formatarCodigoDosagem(codigo);
  const texto = String(valor || "").trim().toUpperCase();
  const revisao = String(numeroRevisao || "0").replace(/\D/g, "") || "0";
  const complemento = texto
    .replace(/^T\d+(?:\.[A-Z0-9]{1,5})?\s*[-/]?\s*/i, "")
    .replace(/^REV\.?\s*\d+\s*[-/]?\s*/i, "")
    .replace(/^-+|-+$/g, "")
    .trim();

  return [codigoFormatado === "-" ? "" : codigoFormatado, `REV.${revisao}`, complemento]
    .filter(Boolean)
    .join("-");
}

function codigoMaterial(item = {}) {
  return item?.codigoFornecedor || item?.fornecedorCodigo || item?.codigoMaterial || item?.codigo || "";
}

function rotuloMaterialCadastro(item) {
  if (!item) return "";
  return `${item.codigo || ""} - ${item.Descricao || item["Descrição"] || item["DescriÃ§Ã£o"] || ""} - ${item.Fornecedor || ""}`;
}

function filtrarMateriaisCaracterizacao(options, { inputValue }, materiaisPorId) {
  const termo = String(inputValue || "").trim().toLocaleLowerCase("pt-BR");
  if (!termo) return options;

  if (/^\d+$/.test(termo)) {
    return options.filter((option) =>
      String(codigoMaterial(option) || "").trim().startsWith(termo)
    );
  }

  return options.filter((option) =>
    `${codigoMaterial(option)} - ${descricaoMaterialCadastrado(option, materiaisPorId)} - ${fornecedorMaterialCadastrado(option, materiaisPorId)}`
      .toLocaleLowerCase("pt-BR")
      .includes(termo)
  );
}

function materialPertenceObra(item, obraId) {
  if (!obraId) return true;
  const obraMaterialId = item.ObraId || item.obraId;
  return !obraMaterialId || obraMaterialId === "__todas_obras__" || obraMaterialId === obraId;
}

function materialEhAgua(item) {
  const descricao = String(item?.Descricao || item?.["Descrição"] || item?.["DescriÃ§Ã£o"] || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
  return item?.IsAgua === true || descricao === "agua";
}

function selecionarNaOrdemDosIds(lista = [], ids = []) {
  const itensPorId = new Map(lista.map((item) => [item.id, item]));
  return ids.map((id) => itensPorId.get(id)).filter(Boolean);
}

function descricaoMaterialCadastrado(item, materiaisPorId = new Map()) {
  const materialCadastrado = materiaisPorId.get(
    item.fornecedorId || item.materialId || item.materialCadastroId
  );

  return (
    materialCadastrado?.Descricao ||
    materialCadastrado?.["Descrição"] ||
    materialCadastrado?.["DescriÃ§Ã£o"] ||
    item.descricao ||
    item.Descricao ||
    item["Descrição"] ||
    item["DescriÃ§Ã£o"] ||
    item.amostra ||
    ""
  );
}

function fornecedorMaterialCadastrado(item, materiaisPorId = new Map()) {
  const materialCadastrado = materiaisPorId.get(
    item.fornecedorId || item.materialId || item.materialCadastroId
  );

  return (
    materialCadastrado?.Fornecedor ||
    item.procedencia ||
    item.fornecedor ||
    item.Fornecedor ||
    ""
  );
}

function resumoMaterial(item, materiaisPorId = new Map()) {
  return {
    id: item.id,
    codigo: codigoMaterial(item),
    materialCadastroId:
      item.fornecedorId || item.materialId || item.materialCadastroId || "",
    descricao: descricaoMaterialCadastrado(item, materiaisPorId),
    procedencia: fornecedorMaterialCadastrado(item, materiaisPorId),
    localColeta: item.localColeta || "",
    tipoAgregado: item.tipoAgregado || "graudo",
    densidadeMedia: item.densidade?.densidadeMedia || "",
    mediaMassaUnitaria: item.massaUnitaria?.mediaMassaUnitaria || "",
  };
}

function calcularProximoCodigo(lista) {
  const numeros = lista
    .map((item) => Number(numeroDosagem(item.codigo)))
    .filter(Number.isFinite);
  const proximo = numeros.length ? Math.max(...numeros) + 1 : lista.length + 1;
  return String(proximo);
}

export default function CadastroDosagem() {
  const { salvandoCadastro, executarSalvamento } = useEstadoSalvamento();
  const history = useHistory();
  const { podeEditarDosagem } = usePermissoesAcesso();
  const somenteLeitura = !podeEditarDosagem;
  const [clientes, setClientes] = useState([]);
  const [obras, setObras] = useState([]);
  const [caracterizacoes, setCaracterizacoes] = useState([]);
  const [materiaisCadastro, setMateriaisCadastro] = useState([]);
  const [dosagens, setDosagens] = useState([]);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [filtro, setFiltro] = useState("");
  const [paginaDosagens, setPaginaDosagens] = useState(0);
  const [itensPorPaginaDosagens, setItensPorPaginaDosagens] = useState(20);
  const [openDialog, setOpenDialog] = useState(false);
  const [dosagemEditando, setDosagemEditando] = useState(null);
  const [modoClonagem, setModoClonagem] = useState(false);
  const [clonarTudo, setClonarTudo] = useState(false);
  const [dosagemOrigemClone, setDosagemOrigemClone] = useState(null);
  const [form, setForm] = useState(estadoInicial);
  const [buscaMaterial, setBuscaMaterial] = useState("");

  useEffect(() => {
    carregarDados();
  }, []);

  async function carregarDados() {
    const [clientesSnap, obrasSnap, caracterizacoesSnap, materiaisCadastroSnap, dosagensSnap] = await Promise.all([
      getDocs(consultaTenantAtual(query(collection(db, "clientes"), orderBy("nome")))),
      getDocs(consultaTenantAtual(query(collection(db, "obras"), orderBy("nome")))),
      getDocs(consultaTenantAtual(query(collection(db, "tblCaracterizacaoAgregados"), orderBy("criadoEm", "desc")))),
      getDocs(consultaTenantAtual(query(collection(db, "tblListaMateriaisCadastrados"), orderBy("codigo", "desc")))),
      getDocs(consultaTenantAtual(query(collection(db, "tblDosagensConcreto"), orderBy("criadoEm", "desc")))),
    ]);

    setClientes(clientesSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setObras(obrasSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setCaracterizacoes(caracterizacoesSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setMateriaisCadastro(materiaisCadastroSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setDosagens(dosagensSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setCarregandoLista(false);
  }

  const clienteSelecionado = clientes.find((cliente) => cliente.id === form.clienteId) || null;
  const obraSelecionada = obras.find((obra) => obra.id === form.obraId) || null;

  const obrasDoCliente = useMemo(
    () => obras.filter((obra) => !form.clienteId || obra.clienteId === form.clienteId),
    [obras, form.clienteId]
  );

  const materiaisDaObra = useMemo(
    () =>
      caracterizacoes.filter(
        (item) =>
          item.status !== "cancelado" &&
          (form.pesquisarTodosMateriais || !form.obraId || item.obraId === form.obraId)
      ),
    [caracterizacoes, form.obraId, form.pesquisarTodosMateriais]
  );

  const materiaisSelecionados = useMemo(
    () => selecionarNaOrdemDosIds(caracterizacoes, form.materialIds),
    [caracterizacoes, form.materialIds]
  );

  const materiaisCadastroPorId = useMemo(
    () => new Map(materiaisCadastro.map((item) => [item.id, item])),
    [materiaisCadastro]
  );

  const materiaisDaObraFiltrados = useMemo(
    () =>
      filtrarMateriaisCaracterizacao(
        materiaisDaObra,
        { inputValue: buscaMaterial },
        materiaisCadastroPorId
      ),
    [materiaisDaObra, buscaMaterial, materiaisCadastroPorId]
  );

  const cimentosTodos = useMemo(
    () => materiaisCadastro.filter((item) => item.IsCimento === true),
    [materiaisCadastro]
  );

  const aditivosTodos = useMemo(
    () => materiaisCadastro.filter((item) => item.IsAditivo === true),
    [materiaisCadastro]
  );

  const adicoesTodas = useMemo(
    () => materiaisCadastro.filter((item) => item.IsAdicao === true),
    [materiaisCadastro]
  );

  const aguasTodas = useMemo(
    () => materiaisCadastro.filter(materialEhAgua),
    [materiaisCadastro]
  );

  const aguas = useMemo(
    () => aguasTodas.filter((item) => {
      const aguaGlobal =
        item.TodasObras === true ||
        item.ObraId === "__todas_obras__" ||
        item.obraId === "__todas_obras__";

      return (
        (aguaGlobal || !form.clienteId || item.ClienteId === form.clienteId) &&
        materialPertenceObra(item, form.obraId)
      );
    }),
    [aguasTodas, form.clienteId, form.obraId]
  );

  const cimentos = useMemo(
    () =>
      form.pesquisarTodosCimentos
        ? cimentosTodos
        : cimentosTodos.filter((item) => materialPertenceObra(item, form.obraId)),
    [cimentosTodos, form.obraId, form.pesquisarTodosCimentos]
  );

  const aditivos = useMemo(
    () =>
      form.pesquisarTodosAditivos
        ? aditivosTodos
        : aditivosTodos.filter((item) => materialPertenceObra(item, form.obraId)),
    [aditivosTodos, form.obraId, form.pesquisarTodosAditivos]
  );

  const adicoes = useMemo(
    () =>
      form.pesquisarTodosAdicoes
        ? adicoesTodas
        : adicoesTodas.filter((item) => materialPertenceObra(item, form.obraId)),
    [adicoesTodas, form.obraId, form.pesquisarTodosAdicoes]
  );

  const cimentoSelecionado = cimentosTodos.find((item) => item.id === form.cimentoId) || null;
  const aditivosSelecionados = useMemo(
    () => selecionarNaOrdemDosIds(aditivosTodos, form.aditivoIds || []),
    [aditivosTodos, form.aditivoIds]
  );
  const adicoesSelecionadas = useMemo(
    () => selecionarNaOrdemDosIds(adicoesTodas, form.adicaoIds || []),
    [adicoesTodas, form.adicaoIds]
  );
  const aguaSelecionada = aguasTodas.find((item) => item.id === form.aguaId) || null;

  const listaFiltrada = useMemo(() => {
    const termo = normalizarTexto(filtro);

    const lista = termo
      ? dosagens.filter((item) =>
          [
            item.codigo,
            item.clienteNome,
            item.obraNome,
            item.traco,
            item.caldaCimento === true ? "calda de cimento" : "",
            item.tipoConcreto,
            textoPesquisaMaterial(item.cimentoResumo),
            textoPesquisaMaterial(item.aguaResumo),
            formatarListaMateriais(item.adicoesResumo || []),
            formatarListaMateriais(item.aditivosResumo || []),
            formatarListaMateriais(item.materiaisResumo || []),
          ].some((valor) =>
            contemTextoPesquisa(normalizarTexto(valor), termo)
          )
        )
      : dosagens;

    return [...lista].sort(
      (a, b) => Number(numeroDosagem(b.codigo)) - Number(numeroDosagem(a.codigo))
    );
  }, [dosagens, filtro]);

  const paginasDosagens = Math.max(1, Math.ceil(listaFiltrada.length / itensPorPaginaDosagens));
  const listaPaginada = filtro.trim()
    ? listaFiltrada
    : listaFiltrada.slice(
        paginaDosagens * itensPorPaginaDosagens,
        (paginaDosagens + 1) * itensPorPaginaDosagens
      );

  useEffect(() => {
    if (paginaDosagens >= paginasDosagens) {
      setPaginaDosagens(Math.max(0, paginasDosagens - 1));
    }
  }, [paginaDosagens, paginasDosagens]);

  function novaDosagem() {
    if (somenteLeitura) return;

    setBuscaMaterial("");
    setDosagemEditando(null);
    setModoClonagem(false);
    setClonarTudo(false);
    setDosagemOrigemClone(null);
    setForm({
      ...estadoInicial,
    });
    setOpenDialog(true);
  }

  function editarDosagem(dosagem) {
    if (somenteLeitura) return;

    setBuscaMaterial("");
    setDosagemEditando(dosagem);
    setModoClonagem(false);
    setClonarTudo(false);
    setDosagemOrigemClone(null);
    setForm({
      ...estadoInicial,
      ...dosagem,
      codigo: numeroDosagem(dosagem.codigo),
      complementoCodigo: complementoCodigoDosagem(dosagem.codigo),
      numeroRevisao: obterNumeroRevisao(dosagem),
      diametroCp: obterDiametroCpCadastro(dosagem),
      materialIds: Array.isArray(dosagem.materialIds) ? dosagem.materialIds : [],
      aditivoIds: Array.isArray(dosagem.aditivoIds) ? dosagem.aditivoIds : [],
      adicaoIds: Array.isArray(dosagem.adicaoIds) ? dosagem.adicaoIds : [],
      usarTextosAutomaticosObservacoes: dosagem.usarTextosAutomaticosObservacoes === true,
    });
    setOpenDialog(true);
  }

  function criarFormularioClone(dosagem, incluirTudo = false) {
    const dadosClonados = { ...dosagem };

    [
      "id",
      "criadoEm",
      "criadoPor",
      "criadoPorEmail",
      "criadoPorNome",
      "atualizadoEm",
      "atualizadoPor",
      "atualizadoPorEmail",
      "atualizadoPorNome",
    ].forEach((campo) => delete dadosClonados[campo]);

    if (!incluirTudo) {
      delete dadosClonados.relatorioFotografico;
      delete dadosClonados.abatimentoSlump;
      delete dadosClonados.slumpInicial;
      delete dadosClonados.slumpFinal;
      delete dadosClonados.prensaRuptura;
      Object.keys(dadosClonados).forEach((campo) => {
        if (campo.startsWith("idadeRuptura") || campo.startsWith("rupturaCp")) {
          delete dadosClonados[campo];
        }
      });
    }

    return {
      ...estadoInicial,
      ...dadosClonados,
      codigo: incluirTudo ? numeroDosagem(dosagem.codigo) : "",
      complementoCodigo: incluirTudo ? "B" : "",
      data: incluirTudo ? dosagem.data || "" : "",
      numeroRevisao: incluirTudo ? obterNumeroRevisao(dosagem) : "0",
      cancelada: incluirTudo ? dosagem.cancelada === true : false,
      diametroCp: obterDiametroCpCadastro(dosagem),
      materialIds: Array.isArray(dosagem.materialIds) ? dosagem.materialIds : [],
      aditivoIds: Array.isArray(dosagem.aditivoIds) ? dosagem.aditivoIds : [],
      adicaoIds: Array.isArray(dosagem.adicaoIds) ? dosagem.adicaoIds : [],
      usarTextosAutomaticosObservacoes: dosagem.usarTextosAutomaticosObservacoes === true,
    };
  }

  function clonarDosagem(dosagem) {
    if (somenteLeitura) return;

    setBuscaMaterial("");
    setDosagemEditando(null);
    setModoClonagem(true);
    setClonarTudo(false);
    setDosagemOrigemClone(dosagem);
    setForm(criarFormularioClone(dosagem, false));
    setOpenDialog(true);
  }

  function alterarClonarTudo(event) {
    const marcado = event.target.checked;
    setClonarTudo(marcado);
    if (dosagemOrigemClone) {
      setForm(criarFormularioClone(dosagemOrigemClone, marcado));
    }
  }

  function selecionarCliente(cliente) {
    setForm((formAtual) => ({
      ...formAtual,
      clienteId: cliente?.id || "",
    }));
  }

  function selecionarObra(obra) {
    setForm((formAtual) => ({
      ...formAtual,
      obraId: obra?.id || "",
    }));
  }

  async function salvarDosagem(abrirDetalhes = false) {
    if (somenteLeitura) {
      alert("Seu acesso permite apenas visualizar dosagens.");
      return;
    }

    if (!form.clienteId) {
      alert("Selecione o cliente.");
      return;
    }

    if (!form.obraId) {
      alert("Selecione a obra.");
      return;
    }

    if (!form.caldaCimento && !materiaisSelecionados.length) {
      alert("Selecione pelo menos um material.");
      return;
    }

    const diametroCp = Number(String(form.diametroCp || "").replace(",", "."));
    if (!Number.isFinite(diametroCp) || diametroCp <= 0) {
      alert("Informe um diâmetro de CP válido.");
      return;
    }

    if (!String(form.traco || "").trim()) {
      alert("Informe o nome do traço.");
      return;
    }

    const codigoDigitado = numeroDosagem(form.codigo);
    const complementoCodigo = String(form.complementoCodigo || "").trim().toUpperCase();

    if (complementoCodigo && !/^[A-Z0-9]{1,5}$/.test(complementoCodigo)) {
      alert("O complemento deve conter até 5 letras ou números, sem pontos ou espaços.");
      return;
    }

    const proximoCodigoPrevisto = calcularProximoCodigo(dosagens);
    const deveAtualizarContador =
      !dosagemEditando?.id &&
      !complementoCodigo &&
      (!codigoDigitado || codigoDigitado === proximoCodigoPrevisto);
    const codigoBaseDosagem = deveAtualizarContador
      ? String((await gerarSequencial({
          chave: "dosagem_concreto",
          valorMinimo: obterMaiorSequencial(dosagens),
        })).numero)
      : codigoDigitado;
    const codigoDosagem = montarCodigoDosagem(codigoBaseDosagem, complementoCodigo);
    const codigoDuplicado = dosagens.some(
      (item) =>
        item.id !== dosagemEditando?.id &&
        String(item.codigo || "").toUpperCase() === codigoDosagem.toUpperCase()
    );

    if (codigoDuplicado) {
      alert(`O código ${formatarCodigoDosagem(codigoDosagem)} já está sendo utilizado.`);
      return;
    }

    const usuarioAtual = auth.currentUser;
    const dados = {
      ...form,
      motivoCancelamento:
        form.cancelada === true ? String(form.motivoCancelamento || "").trim() : "",
      diametroCp: String(form.diametroCp).trim().replace(".", ","),
      codigo: codigoDosagem,
      complementoCodigo: complementoCodigo || null,
      numeroRevisao:
        String(form.numeroRevisao || "0").replace(/\D/g, "") || "0",
      traco: normalizarNomeTraco(
        codigoDosagem,
        form.numeroRevisao,
        form.traco
      ),
      clienteNome: clienteSelecionado?.nome || form.clienteNome || "",
      obraNome: obraSelecionada?.nome || form.obraNome || "",
      materialIds: form.caldaCimento
        ? []
        : materiaisSelecionados.map((item) => item.id),
      materiaisResumo: form.caldaCimento
        ? []
        : materiaisSelecionados.map((item, index) => ({
            ...resumoMaterial(item, materiaisCadastroPorId),
            ordemSelecao: index,
          })),
      cimentoId: cimentoSelecionado?.id || "",
      cimentoResumo: cimentoSelecionado
        ? {
            id: cimentoSelecionado.id,
            codigo: cimentoSelecionado.codigo || "",
            descricao: cimentoSelecionado.Descricao || "",
            fornecedor: cimentoSelecionado.Fornecedor || "",
            massaEspecifica: cimentoSelecionado.MassaEspecifica || "",
          }
        : null,
      aditivoIds: aditivosSelecionados.map((item) => item.id),
      aditivosResumo: aditivosSelecionados.map((item, index) => ({
        id: item.id,
        codigo: item.codigo || "",
        descricao: item.Descricao || "",
        fornecedor: item.Fornecedor || "",
        massaEspecifica: item.MassaEspecifica || "",
        ordemSelecao: index,
      })),
      adicaoIds: form.caldaCimento
        ? []
        : adicoesSelecionadas.map((item) => item.id),
      adicoesResumo: form.caldaCimento
        ? []
        : adicoesSelecionadas.map((item, index) => ({
            id: item.id,
            codigo: item.codigo || "",
            descricao: item.Descricao || "",
            fornecedor: item.Fornecedor || "",
            massaEspecifica: item.MassaEspecifica || "",
            ordemSelecao: index,
          })),
      aguaId: aguaSelecionada?.id || "",
      aguaResumo: aguaSelecionada
        ? {
            id: aguaSelecionada.id,
            codigo: aguaSelecionada.codigo || "",
            descricao: aguaSelecionada.Descricao || "",
            fornecedor: aguaSelecionada.Fornecedor || "",
            localColeta: aguaSelecionada.LocalColeta || "",
            massaEspecifica: aguaSelecionada.MassaEspecifica || "1,000",
          }
        : null,
      atualizadoEm: new Date(),
      atualizadoPor: usuarioAtual?.uid || null,
      atualizadoPorEmail: usuarioAtual?.email || "",
      atualizadoPorNome: usuarioAtual?.displayName || "",
    };

    let salvo;
    if (dosagemEditando?.id) {
      const dadosSalvos = await atualizarDocumentoComMesclagem({
        db,
        referencia: doc(db, "tblDosagensConcreto", dosagemEditando.id),
        original: dosagemEditando,
        atual: { ...dosagemEditando, ...dados },
        metadados: {
          atualizadoEm: dados.atualizadoEm,
          atualizadoPor: dados.atualizadoPor,
          atualizadoPorEmail: dados.atualizadoPorEmail,
          atualizadoPorNome: dados.atualizadoPorNome,
        },
      });
      salvo = { id: dosagemEditando.id, ...dadosSalvos };
    } else {
      const ref = await addDoc(collection(db, "tblDosagensConcreto"), aplicarTenantAtual({
        ...dados,
        criadoEm: new Date(),
        criadoPor: usuarioAtual?.uid || null,
        criadoPorEmail: usuarioAtual?.email || "",
        criadoPorNome: usuarioAtual?.displayName || "",
      }));
      salvo = { id: ref.id, ...dados };
    }

    setOpenDialog(false);
    setDosagemEditando(null);
    setModoClonagem(false);
    setClonarTudo(false);
    setDosagemOrigemClone(null);
    await carregarDados();

    if (abrirDetalhes) {
      history.push(`/app/cadastro-dosagem/${salvo.id}`);
    }
  }

  return (
    <>
      <Navbar />

      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper className="cabecalho-lista-cadastro" elevation={0}>
        <Typography variant="h6" fontWeight={700}>Dosagem</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>Cadastro e estudo de dosagem experimental de concreto</Typography>
        <Stack direction={{ xs: "column", md: "row" }} alignItems="stretch" spacing={2} mb={3}>
          {!somenteLeitura && (
            <Button variant="contained" onClick={novaDosagem}>+ Nova Dosagem</Button>
          )}
          <TextField
            size="small"
            label="Pesquisar"
            placeholder="Código, cliente, obra, traço, materiais ou tipo"
            value={filtro}
            onChange={(event) => {
              setFiltro(event.target.value);
              setPaginaDosagens(0);
            }}
            sx={{ flex: 1 }}
          />
          <TextField size="small" label="Qtd" value={listaFiltrada.length} InputProps={{ readOnly: true }} sx={{ width: 110 }} />
        </Stack>
        </Paper>

        <Paper sx={{ borderRadius: "8px", overflow: "auto", border: "1px solid #e2e8f0", boxShadow: "none" }}>
          <Table className="lista-cadastro-padrao" size="small" sx={{ minWidth: 1180, tableLayout: "fixed" }}>
            <TableHead sx={{ backgroundColor: "#f8fafc" }}>
              <TableRow>
                <TableCell sx={{ width: 64 }}><strong>Cód.</strong></TableCell>
                <TableCell sx={{ width: 260 }}>Cliente / Obra</TableCell>
                <TableCell sx={{ width: 150 }}>Traço</TableCell>
                <TableCell>Materiais</TableCell>
                <TableCell sx={{ width: 160 }}>Data / Rupturas</TableCell>
                <TableCell align="center" sx={{ width: 160 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listaPaginada.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => history.push(`/app/cadastro-dosagem/${item.id}`)}
                >
                  <TableCell sx={{ width: 64 }}>{formatarCodigoDosagem(item.codigo)}</TableCell>
                  <TableCell sx={{ width: 260, whiteSpace: "normal", wordBreak: "break-word" }}>
                    <Typography component="div" variant="caption" sx={{ color: "#0f2f5f", fontWeight: 700, fontSize: "0.72rem" }}>
                      {item.clienteNome || "-"}
                    </Typography>
                    <Typography component="div" variant="caption" sx={{ color: "#475569", fontWeight: 600, fontSize: "0.7rem" }}>
                      Obra: {item.obraNome || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 150, whiteSpace: "normal", wordBreak: "break-word" }}>
                    <Stack spacing={0.25}>
                      <Typography component="span" variant="caption" sx={{ color: "#0f2f5f", fontWeight: 700, fontSize: "0.72rem" }}>
                        {item.traco || "-"}
                        {item.caldaCimento === true && (
                          <Box component="span" sx={{ color: "#64748b", fontWeight: 800 }}>
                            {" "}(calda de cimento)
                          </Box>
                        )}
                      </Typography>
                      {item.cancelada === true && (
                        <Typography component="span" variant="caption" sx={{ color: "#d32f2f", fontWeight: 800, fontSize: "0.68rem" }}>
                          CANCELADA{item.motivoCancelamento ? ` - ${item.motivoCancelamento}` : ""}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <GrupoMateriais rotulo="Cimento" itens={formatarMateriaisComConsumo(item, "cimento")} destaque />
                      <GrupoMateriais rotulo="Adição" itens={formatarMateriaisComConsumo(item, "adicoesResumo")} />
                      <GrupoMateriais rotulo="Água" itens={formatarMateriaisComConsumo(item, "agua")} />
                      <GrupoMateriais rotulo="Aditivos" itens={formatarMateriaisComConsumo(item, "aditivosResumo")} />
                      <GrupoMateriais rotulo="Agregados" itens={formatarMateriaisComConsumo(item, "materiaisResumo")} />
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ width: 160, verticalAlign: "top" }}>
                    <Stack spacing={0.35}>
                      <Typography
                        component="div"
                        variant="caption"
                        sx={{ color: "#475569", fontWeight: 700, fontSize: "0.7rem" }}
                      >
                        Dosagem: {formatarDataBR(item.data)}
                      </Typography>

                      {datasRupturaDosagem(item).map((ruptura) => {
                        const cor =
                          ruptura.status === "vencida"
                            ? "#d32f2f"
                            : ruptura.status === "hoje"
                              ? "#b8860b"
                              : "#2e7d32";

                        return (
                          <Typography
                            key={`ruptura-${item.id}-${ruptura.index}`}
                            component="div"
                            variant="caption"
                            sx={{
                              color: cor,
                              fontWeight: 700,
                              fontSize: "0.68rem",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {ruptura.idade}d - ruptura: {formatarDataCurtaBR(ruptura.data)}
                          </Typography>
                        );
                      })}
                    </Stack>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 160 }}>
                    <Tooltip title="Visualizar">
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          history.push(`/app/cadastro-dosagem/${item.id}?print=1`, { voltarListaDosagens: true });
                        }}
                      >
                        <VisibilityIcon sx={{ color: "primary.main" }} />
                      </IconButton>
                    </Tooltip>
                    {!somenteLeitura && (
                      <>
                        <Tooltip title="Editar cadastro">
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              editarDosagem(item);
                            }}
                          >
                            <EditIcon sx={{ color: "#0288d1" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Clonar dosagem">
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              clonarDosagem(item);
                            }}
                          >
                            <ContentCopyIcon sx={{ color: "#0288d1" }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <HistoricoCadastro colecao="tblDosagensConcreto" documentoId={item.id} registro={item} />
                  </TableCell>
                </TableRow>
              ))}

              {carregandoLista && <TableRow><TableCell colSpan={6}><Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, py: 4 }}><CircularProgress /><Typography color="text.secondary">Carregando registros...</Typography></Box></TableCell></TableRow>}
              {!carregandoLista && listaFiltrada.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">Nenhuma dosagem cadastrada.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {!filtro.trim() && (
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
              <PaginationSelector
                itensPerPage={itensPorPaginaDosagens}
                setItensPerPage={(valor) => {
                  setItensPorPaginaDosagens(valor);
                  setPaginaDosagens(0);
                }}
              />
              <PaginationComponent
                pages={paginasDosagens}
                currentPage={paginaDosagens}
                setCurrentPage={setPaginaDosagens}
              />
            </Stack>
          )}
        </Paper>
      </Box>

      <Dialog open={openDialog} onClose={() => !salvandoCadastro && setOpenDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography component="span" variant="h6">
              {dosagemEditando ? "Editar Dosagem" : "Nova Dosagem"}
            </Typography>
            {modoClonagem && (
              <FormControlLabel
                control={<Checkbox checked={clonarTudo} onChange={alterarClonarTudo} />}
                label="Clonar tudo"
                sx={{ m: 0 }}
              />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(form.caldaCimento)}
                    onChange={(event) => {
                      const marcada = event.target.checked;
                      setForm({
                        ...form,
                        caldaCimento: marcada,
                        diametroCp: marcada ? "50" : "100",
                        ...(marcada
                          ? {
                              materialIds: [],
                              adicaoIds: [],
                              pesquisarTodosMateriais: false,
                              pesquisarTodosAdicoes: false,
                            }
                          : {}),
                      });
                    }}
                  />
                }
                label="Calda de cimento"
              />
              <TextField
                label="Diâmetro CP (mm)"
                value={form.diametroCp || ""}
                onChange={(event) =>
                  setForm({ ...form, diametroCp: event.target.value })
                }
                inputProps={{ inputMode: "decimal" }}
                sx={{ width: 180 }}
              />
              <TextField
                label="Área CP (cm²)"
                value={areaCpPorDiametro(form.diametroCp)}
                InputProps={{ readOnly: true }}
                sx={{ width: 180 }}
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Número da dosagem"
                value={form.codigo || ""}
                onChange={(event) => {
                  const codigo = numeroDosagem(event.target.value);
                  setForm({
                    ...form,
                    codigo,
                    complementoCodigo: codigo ? form.complementoCodigo || "" : "",
                  });
                }}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                helperText={
                  form.codigo
                    ? `Será mostrado nos relatórios como ${formatarCodigoDosagem(
                        montarCodigoDosagem(form.codigo, form.complementoCodigo)
                      )}`
                    : "Deixe vazio para gerar automaticamente."
                }
                fullWidth
              />
              <TextField
                label="Complemento do código (opcional)"
                value={form.complementoCodigo || ""}
                onChange={(event) =>
                  setForm({
                    ...form,
                    complementoCodigo: event.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, 5),
                  })
                }
                placeholder="Ex.: B"
                helperText="Use letras ou números, sem digitar o ponto."
                inputProps={{ maxLength: 5 }}
                disabled={!form.codigo}
                fullWidth
              />
            </Stack>

            <Autocomplete
              options={clientes}
              filterOptions={(options, state) => {
                const pesquisa = state.inputValue.trim();
                if (!pesquisa) return options;

                return options.filter((cliente) =>
                  contemTextoPesquisa(cliente?.nome || "", pesquisa)
                );
              }}
              getOptionLabel={(option) => option?.nome || ""}
              value={clienteSelecionado}
              onChange={(event, value) => selecionarCliente(value)}
              renderInput={(params) => <TextField {...params} label="Cliente" />}
            />

            <Autocomplete
              options={obrasDoCliente}
              getOptionLabel={(option) => option?.nome || ""}
              value={obraSelecionada}
              onChange={(event, value) => selecionarObra(value)}
              renderInput={(params) => <TextField {...params} label="Obra" />}
            />

            <Autocomplete
              options={aguas}
              getOptionLabel={rotuloMaterialCadastro}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={aguaSelecionada}
              onChange={(event, value) =>
                setForm({ ...form, aguaId: value?.id || "" })
              }
              disabled={!form.caldaCimento && (!form.clienteId || !form.obraId)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Água (cadastro de materiais)"
                  helperText={
                    form.obraId && aguas.length === 0
                      ? "Nenhuma água cadastrada para esta obra ou para todas as obras."
                      : ""
                  }
                />
              )}
            />

            <TextField
              label="Data"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.data || ""}
              onChange={(event) => setForm({ ...form, data: event.target.value })}
            />

            {!form.caldaCimento && (
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
                <Autocomplete
                  multiple
                  options={materiaisDaObraFiltrados}
                  inputValue={buscaMaterial}
                  onInputChange={(event, value) => setBuscaMaterial(value)}
                  filterOptions={(options) => options}
                  filterSelectedOptions
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {`${codigoMaterial(option)} - ${descricaoMaterialCadastrado(option, materiaisCadastroPorId)} - ${fornecedorMaterialCadastrado(option, materiaisCadastroPorId)}`}
                    </li>
                  )}
                  getOptionLabel={(option) =>
                    option
                      ? `${codigoMaterial(option)} - ${descricaoMaterialCadastrado(
                          option,
                          materiaisCadastroPorId
                        )} - ${fornecedorMaterialCadastrado(option, materiaisCadastroPorId)}`
                      : ""
                  }
                  value={materiaisSelecionados}
                  onChange={(event, value) => setForm({ ...form, materialIds: value.map((item) => item.id) })}
                  renderInput={(params) => <TextField {...params} label="Materiais (caracterização)" fullWidth />}
                  sx={{ flex: 1, width: "100%" }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(form.pesquisarTodosMateriais)}
                      onChange={(event) =>
                        setForm({ ...form, pesquisarTodosMateriais: event.target.checked })
                      }
                    />
                  }
                  label="Pesquisar todos"
                />
              </Stack>
            )}

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
              <Autocomplete
              options={cimentos}
              getOptionLabel={rotuloMaterialCadastro}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={cimentoSelecionado}
              onChange={(event, value) => setForm({ ...form, cimentoId: value?.id || "" })}
              renderInput={(params) => <TextField {...params} label="Cimento (cadastro de materiais)" />}
                sx={{ flex: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(form.pesquisarTodosCimentos)}
                    onChange={(event) =>
                      setForm({ ...form, pesquisarTodosCimentos: event.target.checked })
                    }
                  />
                }
                label="Pesquisar todos"
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
              <Autocomplete
              multiple
              options={aditivos}
              getOptionLabel={rotuloMaterialCadastro}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={aditivosSelecionados}
              onChange={(event, value) => setForm({ ...form, aditivoIds: value.map((item) => item.id) })}
              renderInput={(params) => <TextField {...params} label="Aditivos (cadastro de materiais)" />}
                sx={{ flex: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(form.pesquisarTodosAditivos)}
                    onChange={(event) =>
                      setForm({ ...form, pesquisarTodosAditivos: event.target.checked })
                    }
                  />
                }
                label="Pesquisar todos"
              />
            </Stack>

            {!form.caldaCimento && (
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
                <Autocomplete
                  multiple
                  options={adicoes}
                  getOptionLabel={rotuloMaterialCadastro}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={adicoesSelecionadas}
                  onChange={(event, value) => setForm({ ...form, adicaoIds: value.map((item) => item.id) })}
                  renderInput={(params) => <TextField {...params} label="Adições (cadastro de materiais)" />}
                  sx={{ flex: 1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(form.pesquisarTodosAdicoes)}
                      onChange={(event) =>
                        setForm({ ...form, pesquisarTodosAdicoes: event.target.checked })
                      }
                    />
                  }
                  label="Pesquisar todos"
                />
              </Stack>
            )}

            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(4, 1fr)" }} gap={2}>
              <TextField
                required
                label="Traço"
                helperText={`Será salvo como ${normalizarNomeTraco(
                  form.codigo,
                  form.numeroRevisao,
                  form.traco || "NOME-DO-TRACO"
                )}`}
                value={form.traco || ""}
                onChange={(event) =>
                  setForm({ ...form, traco: event.target.value })
                }
              />
              <TextField
                required
                label="Nº Revisão"
                value={form.numeroRevisao ?? "0"}
                onChange={(event) =>
                  setForm({
                    ...form,
                    numeroRevisao:
                      event.target.value.replace(/\D/g, ""),
                  })
                }
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              />
              <TextField label="Fator a/c" value={form.fatorAc || ""} onChange={(event) => setForm({ ...form, fatorAc: event.target.value })} />
              <TextField label="Fck (MPa/dias)" value={form.fck || ""} onChange={(event) => setForm({ ...form, fck: event.target.value })} />
              <TextField label="Sd (MPa)" value={form.sd || ""} onChange={(event) => setForm({ ...form, sd: event.target.value })} />
              <TextField label="Slump (mm)" value={form.slump || ""} onChange={(event) => setForm({ ...form, slump: event.target.value })} />
              <TextField
                label="Tolerância do Slump (mm)"
                value={form.toleranciaSlumpProjeto || ""}
                onChange={(event) => setForm({ ...form, toleranciaSlumpProjeto: event.target.value })}
              />
              <TextField label="Tipo de concreto" value={form.tipoConcreto || ""} onChange={(event) => setForm({ ...form, tipoConcreto: event.target.value })} />
              <TextField label="CP's" value={form.cps || ""} onChange={(event) => setForm({ ...form, cps: event.target.value })} />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.cancelada === true}
                    onChange={(event) => {
                      const cancelada = event.target.checked;
                      setForm({
                        ...form,
                        cancelada,
                        motivoCancelamento: cancelada ? form.motivoCancelamento || "" : "",
                      });
                    }}
                  />
                }
                label="Cancelar dosagem"
              />
              {form.cancelada === true && (
                <TextField
                  label="Motivo do cancelamento"
                  value={form.motivoCancelamento || ""}
                  onChange={(event) =>
                    setForm({ ...form, motivoCancelamento: event.target.value })
                  }
                  multiline
                  minRows={2}
                  sx={{ gridColumn: { xs: "1", md: "1 / -1" } }}
                />
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={salvandoCadastro}>Cancelar</Button>
          <Button variant="outlined" onClick={() => executarSalvamento(() => salvarDosagem(false))} disabled={salvandoCadastro}>
            {salvandoCadastro ? "Salvando..." : "Salvar"}
          </Button>
          <Button variant="contained" onClick={() => executarSalvamento(() => salvarDosagem(true))} disabled={salvandoCadastro}>
            {salvandoCadastro ? "Salvando..." : "Salvar e abrir"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
